import React, { useRef, useState } from 'react';
import Icon from '../shared/Icon';
import Timestamp from '../shared/Timestamp';
import './AlertDetailModal.css';
import { apiService } from '../../../services/api';
import {
  COMPARATORS,
  COMPARISON_SHAPES,
  VOLUME_GUARD_DEFAULT_MAX_MATCHES,
} from '../AutomationWizard/alertSpec';
import { storedScheduleLabel } from '../../../utils/scheduleLabel';

/**
 * The version 1 comparison vocabulary, kept here and nowhere else.
 *
 * **This is a display concern, not a live model.** `alertSpec.js` no longer
 * carries the five old comparison types, because nothing authors them any more.
 * But nothing rewrote the stored rows either — that is the whole design of
 * PRD-1320, which resolves a version 1 document on *read* rather than migrating
 * the column — so every alert created before this change is still a version 1
 * document and still runs.
 *
 * This modal is the surface those alerts are looked at on. Dropping the labels
 * would render each of them as a raw enum id, which is a regression in the one
 * place an author goes to find out what their existing alert does. So the
 * labels stay, scoped to the reader that needs them, and marked as what they
 * are: a legend for stored history, not a vocabulary anything can still choose
 * from.
 */
const LEGACY_COMPARISON_LABELS = {
  count_in_window: 'Count in window',
  sum_in_window: 'Sum in window',
  consecutive: 'Consecutive periods',
  date_vs_relation: 'Date vs related record',
  missing_relation: 'Missing related record',
};

const LEGACY_GRAIN_LABELS = {
  member: 'per member',
  site: 'per site',
  shift: 'per shift',
};

/**
 * Which stored shape this is.
 *
 * Discriminated on key presence rather than on `version`, for the reason
 * `stored.py` gives: every shape carries a `version` and they all count from 1.
 * `sql` picks out version 3, `ir` version 2, neither means version 1.
 *
 * Both are described here. A version 2 alert authored last release still runs,
 * and a detail modal that showed nothing for it would read as an alert with no
 * definition rather than as one in the previous format.
 */
// Keyed on the key being *present*, matching `alertPayload.js`. The two files
// held different definitions of version 3 with near-identical comments citing
// the same reasoning, so a document with `sql: ''` was version 3 to the edit form
// and version 1 to this modal — which routed it to `describeLegacySpec` and
// described it as a pre-migration alert, the precise sentence the other file's
// comment calls out. Raised in review of #202.
const isVersionThree = (spec) => !!spec && typeof spec === 'object' && 'sql' in spec;
const isVersionTwo = (spec) => !!spec && typeof spec === 'object' && !!spec.ir;
const hasComparisonDocument = (spec) => isVersionThree(spec) || isVersionTwo(spec);

const comparatorLabel = (id) => COMPARATORS.find((c) => c.id === id)?.label.replace(/\s*\([^)]*\)/, '') ?? id;

/**
 * A version 2 or 3 comparison in words: what the query returned, and what is
 * done with it.
 *
 * Names result columns rather than tables, because that is what the comparison
 * names — and the query beside it is what says where they came from. Stating
 * a table here would be restating something the document does not hold.
 */
const describeComparisonDocument = (spec) => {
  const comparison = spec.comparison ?? {};
  const shape = COMPARISON_SHAPES.find((s) => s.id === comparison.shape);

  if (comparison.shape === 'any_rows') {
    return `${shape?.label ?? comparison.shape}, one match per row returned`;
  }

  const limit = comparison.threshold_column != null
    ? `each subject's own ${comparison.threshold_column}`
    : comparison.threshold;
  const parts = [
    `${comparison.measure_column} ${comparatorLabel(comparison.comparator)} ${limit}`,
  ];
  if (comparison.run_length) {
    const unit = comparison.period_unit ? `${comparison.period_unit}s` : 'periods';
    parts.push(`for ${comparison.run_length} consecutive ${unit}`);
  }
  if (comparison.subject_column) parts.push(`per ${comparison.subject_column}`);
  return parts.join(', ');
};

/**
 * A version 1 comparison in words.
 *
 * Kept verbatim in behaviour for the alerts that still carry this shape. The
 * conditional interpolation matters: the two relational shapes consult neither
 * comparator nor threshold, and interpolating them unconditionally left a
 * dangling comma and a double space.
 */
const describeLegacySpec = (spec) => {
  const label = LEGACY_COMPARISON_LABELS[spec.comparison] ?? spec.comparison;
  const relational = spec.comparison === 'date_vs_relation' || spec.comparison === 'missing_relation';
  const parts = [`${label} on ${spec.source}`];
  if (spec.measure) parts.push(`(${spec.measure})`);
  if (spec.comparator && spec.comparison !== 'missing_relation') {
    parts.push(relational
      ? `${comparatorLabel(spec.comparator)} the related date`
      : `${comparatorLabel(spec.comparator)} ${spec.threshold}`);
  }
  if (spec.run_length) parts.push(`for ${spec.run_length} periods in a row`);
  if (spec.relation?.source) parts.push(`against ${spec.relation.source}`);
  else if (spec.comparison === 'missing_relation') parts.push('with no matching record');
  const grain = LEGACY_GRAIN_LABELS[spec.subject_grain];
  if (grain) parts.push(grain);
  return parts.join(', ');
};

/**
 * A stored comparison in words, whichever shape it was stored in.
 *
 * Discriminated on key presence rather than on `version`, for the reason
 * `stored.py` gives: every shape carries a `version` and they all count from 1,
 * so comparing the numbers would read a version 1 spec as a version 1 alert.
 *
 * Versions 2 and 3 describe identically here, and that is not a coincidence:
 * both hold a query plus a comparison over its result, and this sentence is
 * about the comparison. What differs between them is what the query is stored
 * as, which this line does not read — so the branch is about version 1, which
 * has no query at all and needs the legacy renderer.
 */
const describeStoredSpec = (spec) => (
  hasComparisonDocument(spec) ? describeComparisonDocument(spec) : describeLegacySpec(spec)
);

/**
 * The alert detail disclosure — a **modal**, not a drawer (AC3).
 *
 * A deliberate departure from the run-history drawer the other automation
 * types use. The drawer is a log; this is the alert's current configuration and
 * the place its activation is switched, so it takes focus rather than sliding in
 * beside the list.
 *
 * **History is absent, not broken.** `audit_logs` carries no reference to the
 * automation — no column identifies which automation a row belongs to — so a
 * filtered Activity Log cannot be built. That is PRD-1293. Rather than ship a
 * link that opens an unfiltered log and looks like it worked, the action is
 * shown as unavailable with the reason. AC4 is deferred with it.
 */
const AlertDetailModal = ({ automation, onClose, onShowNotification, onChanged, onEdit }) => {
  const [busy, setBusy] = useState(false);
  // The activation state as this modal last observed it.
  //
  // **Not derived from the prop alone.** `automation` is a snapshot of the row
  // taken when the card was clicked; refreshing the list replaces the array
  // but never re-derives the selected row, so after activating, the modal went
  // on showing "Inactive" and its button went on saying "Activate" — and a
  // second click POSTed /activate again instead of /deactivate.
  //
  // The activation response is the authority on the new state, so it is what
  // this tracks. Reset whenever a different alert is opened.
  const [liveActive, setLiveActive] = useState(null);
  const lastIdRef = useRef(automation?.id);
  if (lastIdRef.current !== automation?.id) {
    lastIdRef.current = automation?.id;
    if (liveActive !== null) setLiveActive(null);
  }

  if (!automation) return null;

  const active = liveActive ?? automation.alert_active === true;
  const severity = automation.alert_severity ?? '—';
  const channels = (automation.alert_channels ?? []).join(', ') || '—';

  // Stored vs effective: a null cap means no cap was ever set and the
  // evaluation default applies. The modal shows what is in force.
  const storedCap = automation.alert_volume_guard_max_matches;
  const capInForce = storedCap == null
    ? `${VOLUME_GUARD_DEFAULT_MAX_MATCHES} (default)`
    : String(storedCap);

  const specs = Array.isArray(automation.alert_spec)
    ? automation.alert_spec
    : automation.alert_spec
      ? [automation.alert_spec]
      : [];

  const handleToggleActive = async () => {
    setBusy(true);
    try {
      const result = await apiService.setAlertActive(automation.id, !active);
      // Trust the response over an optimistic flip: activating a broken alert
      // is refused, so the new state is whatever the server says it is.
      //
      // But only when it actually says. `ActivationResponse` declares
      // `alert_active` as a non-Optional bool, so it should always be there —
      // and `=== true` on an absent field would resolve to false, showing
      // "Inactive" after a successful activation and making the next click
      // activate again. Falling back to the state we asked for keeps a schema
      // change from re-introducing exactly the bug this state was added to fix.
      setLiveActive(
        typeof result?.alert_active === 'boolean' ? result.alert_active : !active
      );
      if (onShowNotification) {
        const blocking = result?.blocking ?? [];
        if (!active && blocking.length > 0) {
          // An empty `blocking` is still not a guarantee — dataset readiness,
          // staleness and service configuration are not checked there — so the
          // wording never promises the alert will fire.
          onShowNotification(
            `Alert activated, but it will not evaluate yet: ${blocking.join(', ')}.`,
            'error'
          );
        } else {
          onShowNotification(active ? 'Alert deactivated' : 'Alert activated', 'success');
        }
      }
      if (onChanged) onChanged();
    } catch (error) {
      // Activating a broken alert is refused; deactivating never is.
      const detail = error?.responseData?.detail;
      const message = typeof detail === 'string' ? detail : error?.message || 'Failed to update alert';
      if (onShowNotification) onShowNotification(message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const rows = [
    ['Description', automation.description || '—'],
    ['Status', active ? 'Active' : 'Inactive'],
    ['Severity', severity],
    ['Channels', channels],
    ['Volume guard', `${capInForce} matches`],
    ['Schedule', storedScheduleLabel(automation)],
    ['Last run', automation.last_run_at ? <Timestamp value={automation.last_run_at} form="long" /> : 'Never'],
  ];

  return (
    <div
      className="alert-modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="alert-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Alert ${automation.name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="alert-modal-header">
          <h2 className="alert-modal-title">{automation.name}</h2>
          <button
            type="button"
            className="alert-modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon name="close" />
          </button>
        </div>

        <div className="alert-modal-body">
          {rows.map(([label, value]) => (
            <div key={label} className="alert-modal-row">
              <span className="alert-modal-label">{label}</span>
              <span className="alert-modal-value">{value}</span>
            </div>
          ))}

          {specs.length > 0 && (
            <div className="alert-modal-spec">
              <span className="alert-modal-label">Comparison</span>
              {specs.map((spec, i) => (
                <p key={spec.alert_id ?? i} className="alert-modal-value">
                  {describeStoredSpec(spec)}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="alert-modal-footer">
          <button
            type="button"
            className="resui-btn resui-btn--secondary"
            disabled
            title="Per-alert history needs PRD-1293: audit_logs has no reference to the automation."
          >
            History
          </button>
          {onEdit && (
            <button
              type="button"
              className="resui-btn resui-btn--secondary"
              onClick={() => onEdit(automation)}
            >
              Edit
            </button>
          )}
          <button
            type="button"
            className={`resui-btn ${active ? 'resui-btn--tertiary' : 'resui-btn--primary'}`}
            onClick={handleToggleActive}
            disabled={busy}
          >
            {busy ? 'Working…' : active ? 'Deactivate' : 'Activate'}
          </button>
        </div>

        <p className="alert-modal-note">
          Per-alert history is not available yet — the audit log does not record
          which automation a row belongs to (PRD-1293).
        </p>
      </div>
    </div>
  );
};

export default AlertDetailModal;
